<template>
	<UApp
		:toaster="{
			position: 'top-right'
		}"
	>
		<!-- Debug panel for development -->
		<ClientOnly>
			<GA4DebugPanel
				v-if="showDebug"
				@close="showDebug = false"
			/>
		</ClientOnly>
		<ClientOnly>
			<LazyBannersAlert
				v-if="showAlertBanner && !$device.isCrawler && route.meta.layout !== ('blank' as RouteMeta['LayoutKey'])"
				:id="alert.id"
				:title="alert.title"
				:message="alert.message"
				:active="alert.active"
				:updated="alert.updated"
				:created-time="alert.createdTime"
				always-show-on-load
			/>
		</ClientOnly>
		<AppHeader
			v-if="route.meta.layout !== ('blank' as RouteMeta['LayoutKey'])"
			:items="items"
			:mobile-items="mobileItems"
		/>

		<UMain
			:class="[
				showAlertBanner ? 'min-h-page-alert' : 'min-h-page-header'
			]"
		>
			<NuxtLayout :show-alert-banner="showAlertBanner">
				<NuxtPage />
			</NuxtLayout>
		</UMain>
	</UApp>
</template>

<script setup lang="ts">
import MyWorker from '@/assets/workers/alertWorker?worker'
import { useAuth } from '@/composables/useAuth'
import { useAlert } from '@/composables/useState'
import { isAlertResponseValid } from '@/helpers/api-response-validators'
import type { NavigationMenuItem } from '@nuxt/ui'
import { useI18n } from 'vue-i18n'
import type { RouteMeta } from 'vue-router'

import { IS_DEVELOPMENT } from '@/constants/index.js'

const {
	intercomSettings
} = useAuth()
const { $intercom } = useNuxtApp()
const localePath = useLocalePath()
const {
	t, locale
} = useI18n({
	useScope: 'global'
})

useHead({
	meta: [
		{
			name: 'viewport',
			content: 'width=device-width, initial-scale=1'
		}
	],
	link: [
		{
			rel: 'icon',
			href: '/favicons/favicon.ico'
		}
	],
	htmlAttrs: {
		lang: () => {
			return locale.value
		}
	}
})

const route = useRoute()

const alert = useAlert()
const worker = ref<Worker | null>(null)

const showAlertBanner = computed(() => {
	return alert.value.active
})

const checkForAlerts = () => {
	worker.value = new MyWorker()
	worker.value.onmessage = res => {
		if (!res.data?.length) {
			alert.value.id = ''
			alert.value.title = ''
			alert.value.message = ''
			alert.value.createdTime = ''
			alert.value.updated = ''
			alert.value.active = false
			return
		}
		if (res?.data?.isError) {
			reportError(res?.data?.error)
			return
		}
		if (isAlertResponseValid(res?.data?.[0])) {
			reportError('Invalid data returned from alerts endpoint')
			return
		}
		const {
			id, title, message, active, createdTime, updated
		} = res.data[0]

		alert.value.id = id.toString() || ''
		alert.value.createdTime = createdTime || ''
		alert.value.updated = updated || ''
		alert.value.title = title || ''
		alert.value.message = message || ''
		alert.value.active = active ?? false
	}
}

onMounted(() => {
	checkForAlerts()
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible' && !worker.value) {
			checkForAlerts()
		} else {
			worker.value?.terminate()
			worker.value = null
		}
	})
})

const items = computed<NavigationMenuItem[]>(() => [
	{
		label: t('links.services'),
		children: [
			{
				label: t('links.residential'),
				description: t('header.residential-description'),
				to: localePath('/residential-internet'),
				active: String (route.name).startsWith('residential-internet')
			},
			{
				label: t('links.business'),
				description: t('header.business-description'),
				to: localePath('/business-internet'),
				active: String (route.name).startsWith('business')
			},
			{
				label: t('links.service-areas'),
				description: t('header.service-areas-description'),
				to: localePath('/service-areas'),
				active: String (route.name).startsWith('service-areas')
			}
		],
		active: String (route.name).startsWith('residential-internet') || String (route.name).startsWith('business') || String (route.name).startsWith('service-areas')
	},
	{
		label: t('links.construction'),
		to: localePath('/installation-overview'),
		active: String (route.name).startsWith('installation-overview')
	},
	{
		label: t('links.about'),
		children: [
			{
				label: t('links.about-us'),
				description: t('header.about-us-description'),
				to: localePath('/about'),
				active: String (route.name).startsWith('about')
			},
			{
				label: t('links.blog'),
				description: t('header.blog-description'),
				to: localePath('/blog'),
				active: String (route.name).startsWith('blog')
			},
			{
				label: t('links.fiber-news'),
				description: t('header.fiber-news-description'),
				to: localePath('/press'),
				active: String (route.name).startsWith('fiber-news')
			},
			{
				label: t('links.careers'),
				description: t('header.careers-description'),
				to: localePath('/careers'),
				active: String (route.name).startsWith('careers')
			}
		],
		active: String (route.name).startsWith('about') || String (route.name).startsWith('blog') || String (route.name).startsWith('fiber-news') || String (route.name).startsWith('careers')
	},
	{
		label: t('links.support'),
		children: [
			{
				label: t('links.help-center'),
				description: t('header.help-center-description'),
				to: 'https://help.maverixbroadband.com/en/',
				target: '_blank',
				active: String (route.name).startsWith('help-center')
			},
			{
				label: t('links.contact-us'),
				description: t('header.contact-us-description'),
				to: localePath('/contact'),
				active: String (route.name).startsWith('contact')
			}
		],
		active: String (route.name).startsWith('help-center') || String (route.name).startsWith('contact')
	},
	{
		label: 'Localization',
		slot: 'localization' as const
	}
] satisfies NavigationMenuItem[])

const mobileItems = computed<NavigationMenuItem[]>(() => [
	{
		label: t('links.services'),
		children: [
			{
				label: t('links.residential'),
				description: t('header.residential-description'),
				to: localePath('/residential-internet'),
				active: String (route.name).startsWith('residential-internet')
			},
			{
				label: t('links.business'),
				description: t('header.business-description'),
				to: localePath('/business-internet'),
				active: String (route.name).startsWith('business')
			},
			{
				label: t('links.service-areas'),
				description: t('header.service-areas-description'),
				to: localePath('/service-areas'),
				active: String (route.name).startsWith('service-areas')
			}
		],
		active: String (route.name).startsWith('residential-internet') || String (route.name).startsWith('business') || String (route.name).startsWith('service-areas')
	},
	{
		label: t('links.construction'),
		children: [
			{
				label: t('links.installation'),
				description: t('header.installation-description'),
				to: localePath('/installation-overview'),
				active: String (route.name).startsWith('installation-overview')
			}
		],
		active: String (route.name).startsWith('installation-overview')
	},
	{
		label: t('links.about'),
		children: [
			{
				label: t('links.about-us'),
				description: t('header.about-us-description'),
				to: localePath('/about'),
				active: String (route.name).startsWith('about')
			},
			{
				label: t('links.blog'),
				description: t('header.blog-description'),
				to: localePath('/blog'),
				active: String (route.name).startsWith('blog')
			},
			{
				label: t('links.fiber-news'),
				description: t('header.fiber-news-description'),
				to: localePath('/press'),
				active: String (route.name).startsWith('fiber-news')
			},
			{
				label: t('links.careers'),
				description: t('header.careers-description'),
				to: localePath('/careers'),
				active: String (route.name).startsWith('careers')
			}
		],
		active: String (route.name).startsWith('about') || String (route.name).startsWith('blog') || String (route.name).startsWith('fiber-news') || String (route.name).startsWith('careers')
	},
	{
		label: t('links.support'),
		children: [
			{
				label: t('links.help-center'),
				description: t('header.help-center-description'),
				to: 'https://help.maverixbroadband.com/en/',
				target: '_blank',
				active: String (route.name).startsWith('help-center')
			},
			{
				label: t('links.contact-us'),
				description: t('header.contact-us-description'),
				to: localePath('/contact'),
				active: String (route.name).startsWith('contact')
			}
		],
		active: String (route.name).startsWith('help-center') || String (route.name).startsWith('contact')
	},
	{
		label: 'Localization',
		slot: 'localization' as const
	}
] satisfies NavigationMenuItem[])

const title = 'Maverix Broadband - Fiber-fast internet, no start-up fees'
const description = 'Maverix Broadband is a fiber internet provider that offers fast, reliable internet with no start-up fees. Experience the difference with Maverix today!'

useSeoMeta({
	title,
	description,
	ogTitle: title,
	ogDescription: description,
	ogImage: 'https://ui-pro-starter.nuxt.dev/social-card.png',
	twitterImage: 'https://ui-pro-starter.nuxt.dev/social-card.png',
	twitterCard: 'summary_large_image'
})

watchEffect(() => {
	if ($intercom?.isReady()) {
		$intercom?.update(intercomSettings.value)
	}
})

onMounted(() => {
	const chatLoadTime = String (route.name).startsWith('contact') ? 1000 : 5000
	nextTick(() => {
		setTimeout(() => {
			$intercom?.boot(intercomSettings.value)
		}, chatLoadTime)
	})
})

const showDebug = ref(false)

// Development keyboard shortcut
if (IS_DEVELOPMENT) {
	onMounted(() => {
		const handleKeypress = (e: KeyboardEvent) => {
			if (e.ctrlKey && e.shiftKey && e.key === 'G') {
				showDebug.value = !showDebug.value
			}
		}
		window.addEventListener('keydown', handleKeypress)
		onUnmounted(() => window.removeEventListener('keydown', handleKeypress))
	})
}
</script>
